import React from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Lightbulb, Cpu, Building2, Lock } from "lucide-react";

export default function AboutPage() {
  useScrollReveal();
  return (
    <WebsiteLayout>
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="about-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">About Tychi</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Bridging Expertise to Transform <strong>Fund Administration</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 600 }}>
            Tychi is the proprietary, AI-enabled technology behind our managed fund administration services — 
            built by a team of Chartered Accountants, Engineers, and Legal Professionals with deep experience in fund operations.
          </p>
        </section>
      </div>

      {/* Company & Security Info Cards */}
      <section className="w-section" style={{ paddingTop: "4rem", paddingBottom: "2rem" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <div className="w-about-info-card w-reveal" data-testid="about-company-card">
            <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", marginBottom: "1.25rem" }}>
              <div className="w-about-info-icon">
                <Building2 size={22} />
              </div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: 600, color: "var(--w-text)", margin: 0 }}>Company</h3>
            </div>
            <p style={{ color: "var(--w-text-muted)", fontSize: "1.0625rem", lineHeight: 1.75, margin: 0 }}>
              Tychi is our proprietary, AI-enabled technology platform for institutional fund administration. It is not sold as standalone software — it powers the managed fund administration services we deliver.
            </p>
            <div className="w-about-fundtec" data-testid="fundtec-branding">
              <span>A product of</span>
              <a href="https://www.fundtec.co" target="_blank" rel="noopener noreferrer">
                <img src="/fundtec-logo.png" alt="Fundtec" className="w-fundtec-logo-light" />
              </a>
            </div>
          </div>
          <div className="w-about-info-card w-about-info-card--muted w-reveal w-delay-1" data-testid="about-security-card">
            <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", marginBottom: "1.25rem" }}>
              <div className="w-about-info-icon">
                <Lock size={22} />
              </div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: 600, color: "var(--w-text)", margin: 0 }}>Security</h3>
            </div>
            <p style={{ color: "var(--w-text-muted)", fontSize: "1.0625rem", lineHeight: 1.75, margin: 0 }}>
              Your information is protected with institutional security standards. We do not share or sell your data to third parties.
            </p>
          </div>
        </div>
      </section>

      {/* Why Tychi */}
      <section className="w-section-full w-section-surface">
        <div className="w-section" style={{ paddingTop: "6rem", paddingBottom: "6rem" }}>
          <div className="w-split">
            <div className="w-reveal-left">
              <div className="w-label">Purpose</div>
              <h2 className="w-title" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
                Why Tychi Was Built
              </h2>
              <p style={{ color: "var(--w-text-muted)", lineHeight: 1.8 }}>
                Traditional fund administration systems were built for a different era. Fragmented 
                tools, manual processes, and disconnected data create operational risks and 
                inefficiencies that modern institutional operations cannot afford.
              </p>
              <p style={{ color: "var(--w-text-muted)", lineHeight: 1.8, marginTop: "1rem" }}>
                Tychi was built to solve this problem. By combining deep domain expertise in fund 
                administration with modern technology architecture, we created a platform that 
                unifies accounting, workflows, and operational management into a single, reliable system.
                It is not sold as standalone software — it powers the managed services we deliver.
              </p>
              <p style={{ color: "var(--w-text-muted)", lineHeight: 1.8, marginTop: "1rem" }}>
                Our cross-disciplinary approach ensures that finance, technology, and legal insights 
                come together to address the real challenges faced by modern fund managers, investors, 
                and administrators.
              </p>
            </div>
            <div className="w-reveal-right w-delay-2">
              <div className="w-grid-2" style={{ gap: "1rem" }}>
                {[
                  { icon: <Target size={28} />, title: "Precision", desc: "Every calculation verified. Every record auditable." },
                  { icon: <Lightbulb size={28} />, title: "Innovation", desc: "Modern architecture solving institutional challenges." },
                  { icon: <Cpu size={28} />, title: "Technology", desc: "Purpose-built infrastructure for financial operations." },
                  { icon: <Building2 size={28} />, title: "Institutional", desc: "Designed for the rigor institutional operations demand." },
                ].map((v, i) => (
                  <div key={i} className="w-card" style={{ padding: "1.5rem" }}>
                    <div style={{ color: "var(--w-blue)", marginBottom: "0.75rem" }}>{v.icon}</div>
                    <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1rem", fontWeight: 500, marginBottom: "0.35rem" }}>{v.title}</h4>
                    <p style={{ fontSize: "0.8125rem", color: "var(--w-text-dim)", lineHeight: 1.5 }}>{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="w-section">
        <div className="w-reveal">
          <div className="w-label">Vision</div>
          <h2 className="w-title" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
            Platform <strong>Vision</strong>
          </h2>
        </div>
        <div className="w-grid-3" style={{ marginTop: "2.5rem" }}>
          {[
            { num: "01", title: "Proprietary Technology", desc: "Custom-built architecture delivers unmatched performance and agility. As your fund evolves, Tychi's in-house engineering allows rapid adaptation and innovation." },
            { num: "02", title: "Robust Security", desc: "Strong encryption, continuous threat monitoring, and strict role-based permissions safeguard sensitive financial information from every angle." },
            { num: "03", title: "Future-Ready Architecture", desc: "From evolving regulatory requirements to emerging asset classes, Tychi's scalable framework is built to adapt and grow alongside you." },
          ].map((v, i) => (
            <div key={i} className={`w-card w-reveal w-delay-${i + 1}`}>
              <div style={{ fontSize: "2.5rem", fontFamily: "'Outfit', sans-serif", fontWeight: 200, color: "var(--w-blue)", marginBottom: "1rem" }}>{v.num}</div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: 500, marginBottom: "0.75rem" }}>{v.title}</h3>
              <p style={{ color: "var(--w-text-muted)", lineHeight: 1.7, fontSize: "0.9375rem" }}>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Commitment */}
      <section className="w-section-full w-section-surface">
        <div className="w-section w-reveal" style={{ paddingTop: "6rem", paddingBottom: "6rem" }}>
          <div className="w-label">Our Commitment</div>
          <h2 className="w-title" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", maxWidth: 600 }}>
            Committed to Your <strong>Success</strong>
          </h2>
          <p style={{ color: "var(--w-text-muted)", lineHeight: 1.8, maxWidth: 800, marginTop: "1.5rem" }}>
            We believe in being a true partner to our clients. From day one, our specialized team 
            works alongside you to tailor Tychi's capabilities to your unique requirements. We remain 
            dedicated to continuous improvement, staying ahead of evolving industry standards.
          </p>
          <p style={{ color: "var(--w-text-muted)", lineHeight: 1.8, maxWidth: 800, marginTop: "1rem" }}>
            Whether you're navigating new regulations or scaling to meet growing demands, our 
            promise is to deliver reliable expertise, responsive support, and a platform that 
            grows with your ambitions.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="w-cta-section">
        <div className="w-reveal">
          <h2 className="w-title">Ready to Get <strong>Started</strong>?</h2>
          <p className="w-subtitle" style={{ margin: "0 auto 2rem", maxWidth: 400 }}>Speak with our team to learn how Tychi powers our managed fund administration services.</p>
          <Link to="/contact" className="w-btn w-btn-primary" data-testid="about-contact-btn">Schedule a Consultation <ArrowRight size={16} /></Link>
        </div>
      </section>
    </WebsiteLayout>
  );
}
